<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="6">
        <v-select
          :items="softwares"
          v-model="selectedSoftware"
          :loading="loadingSoftwares"
          label="Software"
          item-title="name"
          item-value="id"
          @update:model-value="onSoftwareChange"
        />
      </v-col>
      <v-col cols="12" md="6" class="text-right">
        <v-btn
          color="primary"
          @click="openUploadDialog"
          :disabled="!selectedSoftware"
        >
          Nova Versão
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="versions"
          :loading="loadingVersions"
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          :items-length="totalItems"
          :items-per-page-options="[
            { value: 10, title: '10' },
            { value: 20, title: '20' },
            { value: 30, title: '30' },
            { value: 40, title: '40' },
            { value: 50, title: '50' },
          ]"
        >
          <template v-slot:item.size_bytes="{ item }">
            {{ formatFileSize(item.size_bytes) }}
          </template>
          <template v-slot:item.sha256_hash="{ item }">
            <code class="text-caption">{{ item.sha256_hash }}</code>
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
          <template v-slot:item.is_active="{ item }">
            <v-chip
              :color="item.is_active ? 'success' : 'default'"
              size="small"
            >
              {{ item.is_active ? "Ativo" : "Inativo" }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex ga-1 justify-end">
              <v-btn
                variant="text"
                icon
                size="small"
                :loading="downloadingId === item.id"
                :disabled="isRowBusy(item.id)"
                @click.stop="onDownload(item)"
                title="Baixar binário"
              >
                <v-icon :icon="mdiDownload" size="small" />
              </v-btn>
              <v-btn
                v-if="item.is_active"
                variant="text"
                icon
                size="small"
                color="error"
                :loading="updatingId === item.id"
                :disabled="isRowBusy(item.id)"
                @click.stop="confirmToggleActive(item)"
                title="Despublicar versão (rollback)"
              >
                <v-icon :icon="mdiArchiveArrowDownOutline" size="small" />
              </v-btn>
              <v-btn
                v-else
                variant="text"
                icon
                size="small"
                color="success"
                :loading="updatingId === item.id"
                :disabled="isRowBusy(item.id)"
                @click.stop="confirmToggleActive(item)"
                title="Restaurar versão"
              >
                <v-icon :icon="mdiRestore" size="small" />
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Upload Dialog -->
    <v-dialog v-model="uploadDialog" max-width="600" :persistent="uploading">
      <v-card>
        <v-toolbar flat color="transparent">
          <v-toolbar-title>Nova Versão</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-form
            ref="uploadFormRef"
            :disabled="uploading || canRetryFinalize"
            @submit.prevent="onPrimaryAction"
          >
            <v-row>
              <v-col cols="12">
                <v-text-field
                  :model-value="getSelectedSoftwareName()"
                  label="Software"
                  readonly
                  disabled
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="uploadForm.version_code"
                  :rules="[
                    validationRules.required,
                    validationRules.numeric,
                    validationRules.min(1),
                  ]"
                  label="Código da Versão"
                  name="version_code"
                  type="number"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="uploadForm.version_name"
                  :rules="[validationRules.required]"
                  label="Nome da Versão"
                  name="version_name"
                />
              </v-col>
              <v-col cols="12">
                <v-file-input
                  v-model="uploadForm.file"
                  :rules="[validationRules.required, fileNotEmptyRule]"
                  label="Arquivo do Software"
                  name="file"
                  show-size
                />
              </v-col>
            </v-row>
          </v-form>

          <template v-if="uploading">
            <v-progress-linear
              :model-value="uploadProgress"
              :indeterminate="uploadStage !== 'uploading'"
              color="primary"
              height="6"
              rounded
              class="mt-2"
            />
            <div class="text-caption text-medium-emphasis mt-2">
              {{ uploadStatusLabel }}
            </div>
          </template>

          <v-alert
            v-if="uploadError"
            type="error"
            variant="tonal"
            density="compact"
            class="mt-4"
            :text="uploadError"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            variant="text"
            @click="onCloseUploadDialog"
            :disabled="uploading"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            @click="onPrimaryAction"
            :loading="uploading"
            :disabled="!uploadForm.file"
          >
            {{ canRetryFinalize ? "Registrar versão" : "Enviar" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Yank / Restore Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="480" :persistent="updating">
      <v-card v-if="versionToToggle">
        <v-toolbar flat color="transparent">
          <v-toolbar-title>
            {{ versionToToggle.is_active ? "Despublicar versão" : "Restaurar versão" }}
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <template v-if="versionToToggle.is_active">
            Tem certeza de que deseja despublicar a versão
            <strong>{{ versionToToggle.version_name }}</strong>
            (código {{ versionToToggle.version_code }})? Ela deixará de ser
            oferecida aos dispositivos e a versão ativa anterior passará a ser a
            mais recente.
          </template>
          <template v-else>
            Tem certeza de que deseja restaurar a versão
            <strong>{{ versionToToggle.version_name }}</strong>
            (código {{ versionToToggle.version_code }})? Ela voltará a ser
            oferecida aos dispositivos.
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            @click="confirmDialog = false"
            :disabled="updating"
          >
            Cancelar
          </v-btn>
          <v-btn
            :color="versionToToggle.is_active ? 'error' : 'success'"
            :loading="updating"
            @click="onConfirmToggleActive"
          >
            {{ versionToToggle.is_active ? "Despublicar" : "Restaurar" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import {
  useSoftwaresQuery,
  useSoftwareVersionsQuery,
  useCreateUploadUrlMutation,
  useFinalizeVersionMutation,
  useUpdateVersionActiveMutation,
  useDownloadVersionMutation,
  uploadVersionFile,
  UPLOAD_CONTENT_TYPE,
} from "@/queries/modules/software";
import { validationRules } from "@/utils/validation";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useSnackbar } from "@/plugins/snackbar";
import { mdiDownload, mdiArchiveArrowDownOutline, mdiRestore } from "@mdi/js";

const $snackbar = useSnackbar();

const headers = [
  { title: "Data de Criação", key: "created_at" },
  { title: "Nome da Versão", key: "version_name" },
  { title: "Código da Versão", key: "version_code" },
  { title: "Tamanho do Arquivo", key: "size_bytes" },
  { title: "Hash SHA256", key: "sha256_hash" },
  { title: "Status", key: "is_active" },
  { title: "Ações", key: "actions", sortable: false, align: "end" },
];

const uploadFormRef = ref(null);
const selectedSoftware = ref(null);
const uploadDialog = ref(false);
const page = ref(1);
const itemsPerPage = ref(10);
const uploadForm = ref({
  version_code: "",
  version_name: "",
  file: null,
});

// TanStack Query hooks
const softwaresQuery = useSoftwaresQuery();
const softwares = computed(() => softwaresQuery.data.value || []);
const loadingSoftwares = computed(() => softwaresQuery.isLoading.value);

const versionParams = computed(() => ({
  software_id: selectedSoftware.value,
  // List yanked (inactive) versions too so they can be restored from this screen.
  active_only: false,
  page: page.value,
  per_page: itemsPerPage.value,
}));
const versionsQuery = useSoftwareVersionsQuery(versionParams);
const versions = computed(() => versionsQuery.data.value || []);
const loadingVersions = computed(() => versionsQuery.isLoading.value);
const totalItems = computed(() => versions.value.length);

// The upload runs in three steps — presign, PUT straight to S3, then register the object with the
// API. The stage drives the progress display and decides what a retry has to redo: a failed
// transfer restarts everything, a failed registration only has to be re-registered.
const uploadStage = ref("idle"); // idle | presigning | uploading | finalizing | finalize-failed
const uploadProgress = ref(0);
const uploadedBytes = ref(0);
const uploadTotalBytes = ref(0);
const uploadError = ref("");
// Finalize payload, kept while a registration retry is on offer so it matches the sent bytes.
const pendingFinalize = ref(null);

const createUploadUrlMutation = useCreateUploadUrlMutation();
const finalizeVersionMutation = useFinalizeVersionMutation();

const uploading = computed(() =>
  ["presigning", "uploading", "finalizing"].includes(uploadStage.value),
);
const canRetryFinalize = computed(() => uploadStage.value === "finalize-failed");

const uploadStatusLabel = computed(() => {
  switch (uploadStage.value) {
    case "presigning":
      return "Preparando envio";
    case "uploading":
      // Bytes alongside the percentage: on the slow links this whole flow exists for, a lone
      // percentage moves too rarely to look alive.
      return uploadTotalBytes.value
        ? `Enviando arquivo — ${uploadProgress.value}% (${formatFileSize(uploadedBytes.value)} de ${formatFileSize(uploadTotalBytes.value)})`
        : "Enviando arquivo";
    case "finalizing":
      return "Registrando versão";
    default:
      return "";
  }
});

// A zero-byte package is rejected by the API only after the whole round trip, so catch it here.
const fileNotEmptyRule = (value) => {
  const file = Array.isArray(value) ? value[0] : value;
  return !file || file.size > 0 || "Arquivo vazio";
};

// Yank / restore and download actions. The whole /software route is already gated by the
// "ops:software:upload" scope these endpoints require, so no per-action permission check is needed.
const updateActiveMutation = useUpdateVersionActiveMutation();
const updating = computed(() => updateActiveMutation.isPending.value);
const downloadMutation = useDownloadVersionMutation();

const updatingId = ref(null);
const downloadingId = ref(null);
const confirmDialog = ref(false);
const versionToToggle = ref(null);

const isRowBusy = (id) => updatingId.value === id || downloadingId.value === id;

// Auto-select first software when list loads
watch(
  softwares,
  (newSoftwares) => {
    if (newSoftwares.length > 0 && !selectedSoftware.value) {
      selectedSoftware.value = newSoftwares[0].id;
    }
  },
  { immediate: true },
);

const onSoftwareChange = () => {
  page.value = 1;
  // Query will auto-refetch due to reactive params
};

const getSelectedSoftwareName = () => {
  const software = softwares.value.find((s) => s.id === selectedSoftware.value);
  return software ? software.name : "";
};

const openUploadDialog = () => {
  resetUploadForm();
  prefillUploadForm();
  uploadDialog.value = true;
};

const prefillUploadForm = () => {
  if (versions.value.length > 0) {
    // Find the most recent version (highest version_code)
    const latestVersion = versions.value.reduce((latest, current) => {
      return current.version_code > latest.version_code ? current : latest;
    });

    // Prefill with incremented version_code and same version_name
    uploadForm.value.version_code = latestVersion.version_code + 1;
    uploadForm.value.version_name = latestVersion.version_name;
  } else {
    // If no versions exist, start with defaults
    uploadForm.value.version_code = 1;
    uploadForm.value.version_name = "";
  }
};

const onPrimaryAction = () =>
  canRetryFinalize.value ? runFinalize() : onSubmitUpload();

// Short pt-BR fragment for an API failure. The API's own 4xx details are already pt-BR and read
// well verbatim; the taken-version-code conflict, transport errors and 5xx are not, so they get
// their own wording here.
const describeApiError = (e) => {
  if (!e.response) return "falha de comunicação com o servidor";
  if (e.response.status === 409) {
    return "já existe uma versão com esse código, use um código maior";
  }
  if (e.response.status >= 500) return "o servidor não concluiu a operação";
  return e.message;
};

const onSubmitUpload = async () => {
  const { valid } = await uploadFormRef.value.validate();
  if (!valid) return;

  const file = uploadForm.value.file;
  const software_id = selectedSoftware.value;
  const version_code = Number(uploadForm.value.version_code);
  const version_name = uploadForm.value.version_name;

  uploadError.value = "";
  pendingFinalize.value = null;

  // Step 1 — presign. An unknown software or a taken version code surfaces here, before any bytes
  // move, so the user is not made to sit through a 60MB transfer just to be told it was rejected.
  uploadStage.value = "presigning";
  let uploadUrl;
  try {
    ({ upload_url: uploadUrl } = await createUploadUrlMutation.mutateAsync({
      software_id,
      version_code,
    }));
  } catch (e) {
    uploadStage.value = "idle";
    uploadError.value = `Não foi possível preparar o envio: ${describeApiError(e)}.`;
    return;
  }

  // Step 2 — the bytes go straight to storage, bypassing the API entirely.
  uploadStage.value = "uploading";
  uploadProgress.value = 0;
  uploadedBytes.value = 0;
  uploadTotalBytes.value = file.size;
  try {
    await uploadVersionFile(uploadUrl, file, ({ percent, loaded, total }) => {
      uploadProgress.value = percent;
      uploadedBytes.value = loaded;
      uploadTotalBytes.value = total;
    });
  } catch (e) {
    uploadStage.value = "idle";
    uploadError.value = `Não foi possível enviar o arquivo: ${e.message}.`;
    return;
  }

  // Step 3 — register the stored object. Same software_id/version_code as step 1: the storage key
  // is derived from them.
  pendingFinalize.value = {
    software_id,
    version_code,
    version_name,
    filename: file.name,
    content_type: UPLOAD_CONTENT_TYPE,
  };
  await runFinalize();
};

const runFinalize = async () => {
  uploadStage.value = "finalizing";
  uploadError.value = "";

  try {
    await finalizeVersionMutation.mutateAsync(pendingFinalize.value);
    $snackbar.success("Versão enviada com sucesso");
    uploadDialog.value = false;
    resetUploadForm();
  } catch (e) {
    const status = e.response?.status;
    if (status >= 400 && status < 500) {
      // The request itself is wrong — the code was taken meanwhile, or the object never landed.
      // Repeating it unchanged cannot help, so go back to the form and let the user adjust.
      uploadStage.value = "idle";
      pendingFinalize.value = null;
      uploadError.value = `O arquivo foi enviado, mas o registro da versão falhou: ${describeApiError(e)}.`;
    } else {
      // Transient. The bytes are already stored, so the retry skips straight to registering.
      uploadStage.value = "finalize-failed";
      uploadError.value = `O arquivo foi enviado, mas o registro da versão falhou: ${describeApiError(e)}. O arquivo continua no armazenamento.`;
    }
  }
};

const onCloseUploadDialog = () => {
  uploadDialog.value = false;
  resetUploadForm();
};

const resetUploadForm = () => {
  uploadForm.value = {
    version_code: "",
    version_name: "",
    file: null,
  };
  uploadStage.value = "idle";
  uploadProgress.value = 0;
  uploadedBytes.value = 0;
  uploadTotalBytes.value = 0;
  uploadError.value = "";
  pendingFinalize.value = null;
  uploadFormRef.value?.resetValidation();
};

const confirmToggleActive = (item) => {
  versionToToggle.value = item;
  confirmDialog.value = true;
};

const onConfirmToggleActive = async () => {
  const version = versionToToggle.value;
  if (!version) return;

  const yanking = version.is_active;
  updatingId.value = version.id;
  try {
    await updateActiveMutation.mutateAsync({
      version_id: version.id,
      is_active: !version.is_active,
    });
    $snackbar.success(
      yanking
        ? "Versão despublicada com sucesso"
        : "Versão restaurada com sucesso",
    );
    confirmDialog.value = false;
    versionToToggle.value = null;
  } catch (e) {
    $snackbar.error(`Erro ao atualizar versão: ${e.message}`);
  } finally {
    updatingId.value = null;
  }
};

const onDownload = async (item) => {
  downloadingId.value = item.id;
  try {
    // Request a fresh presigned URL per download — it expires in 15 minutes and must not be cached.
    const { url, filename } = await downloadMutation.mutateAsync(item.id);
    triggerDownload(url, filename);
  } catch (e) {
    $snackbar.error(`Erro ao gerar link de download: ${e.message}`);
  } finally {
    downloadingId.value = null;
  }
};

const triggerDownload = (url, filename) => {
  // The presigned URL already serves the file as an attachment with the original filename via
  // content-disposition; the download attribute is a best-effort hint for same-origin responses.
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.target = "_blank";
  link.rel = "noopener";
  link.click();
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const formatDate = (dateString) => {
  return format(parseISO(dateString), "dd/MM/yyyy HH:mm", { locale: ptBR });
};

// Queries auto-refetch when reactive params change
</script>
